import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Input, ButtonSiguiente } from "@/components/ui";

import { useProfesional } from "../context/ProfesionalContext";
import { LandingData } from "../core/buildLandingData";

const EditarFooterSection: FC = () => {
  const navigate = useNavigate();
 const { profesionalId, profesionId, perfilId} = useParams();
  const { landingData, setLandingData, loading } = useProfesional();

  if (loading) {
    return <div className="p-8 text-white">Cargando footer...</div>;
  }

  if (!landingData) {
    return <div className="p-8 text-red-400">No se pudo cargar la landing</div>;
  }

  const contact = landingData.contact;

  // UPDATE SIMPLE FIELDS
  const handleChange = (field: "phone" | "correo_perfil", value: string) => {
    setLandingData((prev: LandingData | null) => {
      if (!prev) return prev;

      return {
        ...prev,
        contact: {
          ...prev.contact,
          [field]: value,
        },
      };
    });
  };

  // UPDATE SOCIAL
  const handleSocialChange = (
    index: number,
    field: "platform" | "url",
    value: string,
  ) => {
    setLandingData((prev: LandingData | null) => {
      if (!prev) return prev;

      const updatedSocial = [...(prev.contact.social ?? [])];

      updatedSocial[index] = {
        ...updatedSocial[index],
        [field]: value,
      };

      return {
        ...prev,
        contact: {
          ...prev.contact,
          social: updatedSocial,
        },
      };
    });
  };

  // ADD SOCIAL
  const addSocial = () => {
    setLandingData((prev: LandingData | null) => {
      if (!prev) return prev;

      return {
        ...prev,
        contact: {
          ...prev.contact,
          social: [
            ...(prev.contact.social ?? []),
            {
              platform: "",
              url: "",
            },
          ],
        },
      };
    });
  };

  // REMOVE SOCIAL
  const removeSocial = (index: number) => {
    setLandingData((prev: LandingData | null) => {
      if (!prev) return prev;

      const updated = [...(prev.contact.social ?? [])];

      updated.splice(index, 1);

      return {
        ...prev,
        contact: {
          ...prev.contact,
          social: updated,
        },
      };
    });
  };

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-white">
      {/* VOLVER */}
      <div className="absolute top-4 left-4 z-50 pointer-events-auto">
        <ButtonSiguiente
          onClick={() =>
            navigate(
              `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
              {
                state: {
                  showLandingPrincipal: true,
                },
              },
            )
          }
          className="
      px-4 py-2
      rounded-lg
      bg-zinc-900
      border border-white/10
      hover:border-cyan-500
      transition
    "
        >
          ← Volver
        </ButtonSiguiente>
      </div>

      {/* HEADER */}
      <div
        className="
        w-full
        rounded-lg
        border-t-2
        border-b-2
        border-cyan-300
        bg-zinc-950
        py-2
        px-8
        shadow-md
        shadow-zinc-800
        backdrop-blur-xl
        mb-10
        mt-5
      "
      >
        <div
          className="
          flex flex-col
          gap-8
          md:flex-row
          md:items-center
          md:justify-between
        "
        >
          <div className="space-y-3 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              Editor del Footer
            </h1>

            <p className="text-zinc-400 text-sm max-w-md">
              Configura la información de contacto y las redes sociales que se
              mostrarán en el pie de tu landing.
            </p>
          </div>
          <div
            className="
            flex
            flex-col
            sm:flex-row
            gap-3
            w-full
            md:w-auto
          "
          >
            <ButtonSiguiente
              className="
            px-4 py-2
            rounded-lg
            bg-zinc-900
            border border-white/10
            hover:border-cyan-500
            transition
            flex items-center justify-center
            whitespace-nowrap
            overflow-hidden
            text-ellipsis
            max-w-full
            "
            >
              Guardar cambios
            </ButtonSiguiente>
          </div>
        </div>
      </div>
      {/* CONTACTO */}
      <section className={`${card} bg-zinc-900 text-white`}>
        <h2 className="text-xl font-semibold mb-2">Información de contacto</h2>

        <p className="text-zinc-400 text-sm mb-8">
          Estos datos aparecerán en el footer de tu landing.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">
              Teléfono
            </label>

            <Input
              value={contact.phone ?? ""}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">Email</label>

            <Input
              value={contact.correo_perfil ?? ""}
              onChange={(e) => handleChange("correo_perfil", e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* REDES */}
      <section className={`${card} bg-zinc-900 text-white mt-10`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 mb-8">
          <div>
            <h2 className="text-xl font-semibold">Redes sociales</h2>

            <p className="text-zinc-400 text-sm">
              Añade los enlaces de tus perfiles sociales.
            </p>
          </div>

          <ButtonSiguiente
            onClick={addSocial}
            className="
            px-4 py-2
            rounded-lg
            bg-zinc-900
            border border-white/10
            hover:border-green-500
            transition
          "
          >
            + Añadir red social
          </ButtonSiguiente>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {(contact.social ?? []).length === 0 && (
            <section className={card}>
              <p className="text-zinc-500">No hay redes sociales añadidas.</p>
            </section>
          )}

          {(contact.social ?? []).map((s, i) => (
            <section key={i} className={`${card} bg-zinc-900 space-y-5`}>
              <h3 className="text-lg font-semibold">Red {i + 1}</h3>

              <Input
                placeholder="Instagram, Facebook..."
                value={s.platform}
                onChange={(e) =>
                  handleSocialChange(i, "platform", e.target.value)
                }
              />

              <Input
                placeholder="https://..."
                value={s.url}
                onChange={(e) => handleSocialChange(i, "url", e.target.value)}
              />

              <div className="flex justify-end">
                <ButtonSiguiente
                  type="button"
                  onClick={() => removeSocial(i)}
                  className="
                  px-4 py-2
                  rounded-lg
                  bg-zinc-900
                  border border-white/10
                  hover:border-red-500
                  transition
                "
                >
                  Eliminar
                </ButtonSiguiente>
              </div>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
};

export default EditarFooterSection;
